import "@/App.css";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowRight,
  MapPin,
  Search,
  Users,
  Play,
  X,
  Utensils,
  Navigation,
  ShieldCheck,
  Coffee,
  Car,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback venue imagery (used when branch has no image_url yet)
const VENUE_IMAGES = [
  "https://images.unsplash.com/photo-1542204165-65bf26472b9b?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1517604931442-7e0c8ed2963c?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1524985069026-dd778a71c7b4?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1598899134739-24c46f58b8c0?auto=format&fit=crop&w=1200&q=80",
];

const Section = ({ children, className = "" }) => (
  <section className={`max-w-[1280px] mx-auto px-5 sm:px-8 ${className}`}>{children}</section>
);

const GoldButton = ({ children, className = "", ...props }) => (
  <button
    className={`bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold tracking-wide px-6 py-3 rounded-md hover:from-[#f1d59c] hover:to-[#d4ac6f] transition-all shadow-[0_4px_20px_rgba(212,175,116,0.25)] ${className}`}
    {...props}
  >
    {children}
  </button>
);

/* Convert YouTube/Vimeo links to embeddable form, otherwise return raw URL (for mp4). */
const toVideoEmbed = (link) => {
  if (!link) return { type: "none", src: "" };
  const url = link.trim();
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/i);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0` };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vm) return { type: "iframe", src: `https://player.vimeo.com/video/${vm[1]}?autoplay=1` };
  if (/\.(mp4|webm|ogg)(\?|$)/i.test(url)) return { type: "video", src: url };
  return { type: "iframe", src: url };
};

/* Normalise a maps URL so the MAPS button opens something usable in a new tab. */
const toMapsHref = (link) => {
  if (!link) return "";
  const url = link.trim();
  if (/^https?:\/\//i.test(url)) return url;
  return `https://www.google.com/maps?q=${encodeURIComponent(url)}`;
};

/* ---------- Video Modal ---------- */
const VideoModal = ({ link, branchName, onClose }) => {
  const v = toVideoEmbed(link);
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="video-modal"
    >
      <div
        className="relative w-full max-w-4xl aspect-video bg-black rounded-xl overflow-hidden border border-[#d4a574]/30"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 bg-[#1a0f06]/80 hover:bg-[#1a0f06] text-white rounded-full p-2 border border-[#d4a574]/40"
          data-testid="video-modal-close"
          aria-label="Close video"
        >
          <X size={18} />
        </button>
        {v.type === "iframe" ? (
          <iframe
            title={`${branchName} venue tour`}
            src={v.src}
            className="w-full h-full"
            allow="autoplay; encrypted-media; fullscreen"
            allowFullScreen
          />
        ) : v.type === "video" ? (
          <video src={v.src} className="w-full h-full" controls autoPlay />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#bfb393]">
            No video available
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------- Venue Card (new reference-style layout) ---------- */
const VenueCard = ({ branch, cityName, img, slotsAvailable, onWatchVideo }) => {
  const hasVideo = !!branch.video_link;
  const hasMap = !!branch.map_link;
  const hasMenu = !!branch.petpooja_menu_url;

  return (
    <div
      className="rounded-xl overflow-hidden border border-[#3a2a14]/70 bg-gradient-to-b from-[#14100a] to-[#0d0905] hover:border-[#d4a574]/50 transition flex flex-col"
      data-testid={`venue-card-${branch.id}`}
    >
      {/* Image + Watch Video overlay */}
      <div className="relative h-56 sm:h-64 bg-[#1a0f06]">
        <img src={img} alt={branch.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/10" />

        {hasVideo && (
          <button
            onClick={() => onWatchVideo(branch)}
            className="absolute bottom-3 left-3 inline-flex items-center gap-2 bg-[#0a0604]/85 hover:bg-[#0a0604] text-white text-xs font-semibold px-3 py-2 rounded-full border border-white/30 backdrop-blur-sm transition"
            data-testid={`watch-video-btn-${branch.id}`}
            aria-label={`Watch ${branch.name} video`}
          >
            <span className="w-5 h-5 rounded-full bg-white/90 text-[#0a0604] flex items-center justify-center">
              <Play size={11} fill="#0a0604" />
            </span>
            Watch Video
          </button>
        )}
      </div>

      {/* Title row + MAPS/MENU actions */}
      <div className="p-4 sm:p-5 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3
            className="font-display text-white text-xl sm:text-2xl leading-tight truncate"
            data-testid={`venue-name-${branch.id}`}
          >
            {branch.name}
          </h3>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[12.5px] text-[#bfb393]">
              <MapPin size={12} className="text-[#f4d089]" />
              {cityName || branch.address?.split(",").slice(-1)[0]?.trim() || "India"}
            </span>
            <span
              className={`text-[10.5px] font-bold tracking-wider px-2 py-0.5 rounded border ${
                slotsAvailable
                  ? "text-[#7ad99c] border-[#7ad99c]/40 bg-[#1a3a22]/60"
                  : "text-[#ff8a8a] border-[#ff8a8a]/40 bg-[#3a1a1a]/60"
              }`}
              data-testid={`slots-badge-${branch.id}`}
            >
              {slotsAvailable ? "SLOTS AVAILABLE" : "0 SLOTS AVAILABLE"}
            </span>
          </div>
        </div>

        {/* MAPS + MENU icon buttons */}
        <div className="flex gap-2 shrink-0">
          <a
            href={hasMap ? toMapsHref(branch.map_link) : "#"}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => !hasMap && e.preventDefault()}
            className={`flex flex-col items-center justify-center w-14 h-14 rounded-lg border text-[10px] font-semibold tracking-wider transition ${
              hasMap
                ? "border-[#d4a574]/40 text-[#f4d089] hover:bg-[#1a0f06]"
                : "border-[#3a2a14] text-[#5a4f3c] cursor-not-allowed"
            }`}
            data-testid={`venue-maps-${branch.id}`}
            aria-label="Open in Maps"
          >
            <Navigation size={16} />
            <span className="mt-0.5">MAPS</span>
          </a>
          <a
            href={hasMenu ? branch.petpooja_menu_url : "#"}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => !hasMenu && e.preventDefault()}
            className={`flex flex-col items-center justify-center w-14 h-14 rounded-lg border text-[10px] font-semibold tracking-wider transition ${
              hasMenu
                ? "border-[#d4a574]/40 text-[#f4d089] hover:bg-[#1a0f06]"
                : "border-[#3a2a14] text-[#5a4f3c] cursor-not-allowed"
            }`}
            data-testid={`venue-menu-${branch.id}`}
            aria-label="View Menu"
          >
            <Utensils size={16} />
            <span className="mt-0.5">MENU</span>
          </a>
        </div>
      </div>

      {/* Info rows */}
      <div className="px-4 sm:px-5 pb-4 space-y-2 text-[13px] text-[#d8cdb3]">
        {branch.capacity > 0 && (
          <div className="flex items-center gap-2">
            <Users size={13} className="text-[#f4d089]" />
            Max <b className="text-white">{branch.capacity}</b> people
            <span className="mx-2 text-[#3a2a14]">•</span>
            <Coffee size={13} className="text-[#f4d089]" />
            Food &amp; Drinks available
          </div>
        )}
        <div className="flex items-center gap-2">
          <ShieldCheck size={13} className="text-[#7ad99c]" />
          Free Cancellation*
          <span className="mx-2 text-[#3a2a14]">•</span>
          <span className="text-[#bfb393]">Decor ₹750 extra</span>
        </div>
        <div
          className="flex items-center gap-2"
          data-testid={`venue-parking-${branch.id}`}
        >
          <Car size={13} className="text-[#7ad99c]" />
          <span className="text-[#d8cdb3]">Free Parking available</span>
        </div>
        {branch.address && (
          <div className="flex items-start gap-2 text-[12px] text-[#a89878] pt-1">
            <MapPin size={12} className="text-[#a89878] mt-[2px] shrink-0" />
            <span className="line-clamp-2">{branch.address}</span>
          </div>
        )}
      </div>

      {/* Book CTA */}
      <div className="mt-auto p-4 sm:p-5 pt-0">
        <button
          onClick={() => (window.location.href = `/book?branch=${branch.id}`)}
          className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-[#1a0f06] bg-gradient-to-b from-[#e8c98a] to-[#c8a266] hover:from-[#f1d59c] hover:to-[#d4ac6f] px-4 py-2.5 rounded-md transition"
          data-testid={`venue-book-${branch.id}`}
        >
          Book Now <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
};

const Locations = () => {
  const [cities, setCities] = useState([]);
  const [branches, setBranches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [query, setQuery] = useState("");
  const [cityFilter, setCityFilter] = useState("all");
  const [videoBranch, setVideoBranch] = useState(null);
  const [content, setContent] = useState({
    eyebrow: "OUR VENUES",
    heading: "Find The CineVibe venue near you",
    subtitle_fallback: "Premium private screens, opening across India.",
    search_placeholder: "Search by venue or area",
    city_filter_all_label: "All cities",
    loading_text: "Loading venues…",
    no_results_prefix: "No venues matched",
    cta_heading: "Don't see your city?",
    cta_subtitle: "We're expanding fast. Partner with us and bring The CineVibe to your city.",
    cta_franchise_btn: "EXPLORE FRANCHISE",
    cta_book_btn: "BOOK A PRIVATE SCREEN",
    footer_text: "The CineVibe Private Screens & Café",
  });

  useEffect(() => {
    axios.get(`${API}/cities`).then((r) => setCities(r.data || [])).catch(() => setCities([]));
    axios.get(`${API}/branches`).then((r) => setBranches(r.data || [])).catch(() => setBranches([]));
    axios.get(`${API}/content/locations`).then((r) => setContent((p) => ({ ...p, ...(r.data || {}) }))).catch(() => {});
  }, []);

  // Fetch rooms per branch (the /api/rooms endpoint requires branch_id)
  useEffect(() => {
    if (!branches.length) return;
    Promise.all(
      branches.map((b) =>
        axios
          .get(`${API}/rooms`, { params: { branch_id: b.id } })
          .then((r) => r.data || [])
          .catch(() => [])
      )
    ).then((lists) => setRooms(lists.flat()));
  }, [branches]);

  const cityMap = useMemo(() => {
    const m = {};
    cities.forEach((c) => (m[c.id] = c.name));
    return m;
  }, [cities]);

  const roomsByBranch = useMemo(() => {
    const m = {};
    rooms.forEach((r) => {
      if (!m[r.branch_id]) m[r.branch_id] = 0;
      m[r.branch_id] += 1;
    });
    return m;
  }, [rooms]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return branches.filter((b) => {
      if (cityFilter !== "all" && b.city_id !== cityFilter) return false;
      if (!q) return true;
      const cityName = (cityMap[b.city_id] || "").toLowerCase();
      return (
        b.name.toLowerCase().includes(q) ||
        cityName.includes(q) ||
        (b.address || "").toLowerCase().includes(q)
      );
    });
  }, [branches, cityFilter, query, cityMap]);

  return (
    <div className="min-h-screen bg-[#0a0604] text-[#e8dcc0]" data-testid="locations-page">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0a0604]/85 border-b border-[#d4a574]/15">
        <Section className="flex items-center justify-between py-4">
          <a href="/" className="flex items-center" data-testid="cinevibe-logo">
  <div className="w-16 h-16 rounded-full overflow-hidden border border-[#d4a574]/40 flex items-center justify-center bg-[#0a0604]">
    <img src="/images/logo.png" alt="CineVibe logo" className="w-full h-full object-cover" />
  </div>
</a>
          <GoldButton
            data-testid="locations-header-book"
            onClick={() => (window.location.href = "/book")}
            className="text-sm px-5 py-2.5"
          >
            BOOK NOW
          </GoldButton>
        </Section>
      </header>

      <Section className="pt-10 pb-6 text-center">
        <span className="inline-block bg-[#f4d089] text-[#1a0f06] text-[11px] font-bold tracking-wider px-3 py-1 rounded">
          {content.eyebrow}
        </span>
        <h1 className="font-display text-white text-3xl sm:text-5xl mt-4">{content.heading}</h1>
        <p className="text-[#bfb393] mt-3 max-w-xl mx-auto text-[15px]">
          {branches.length > 0
            ? `${branches.length} private-screen venues across ${cities.length} ${cities.length === 1 ? "city" : "cities"}.`
            : content.subtitle_fallback}
        </p>

        <div className="mt-6 max-w-xl mx-auto flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a89878]" />
            <input
              data-testid="locations-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={content.search_placeholder}
              className="w-full bg-[#14100a] border border-[#3a2a14] focus:border-[#d4a574]/60 outline-none rounded-md pl-10 pr-4 py-3 text-sm text-white placeholder:text-[#7a6e54]"
            />
          </div>
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="bg-[#14100a] border border-[#3a2a14] focus:border-[#d4a574]/60 outline-none rounded-md px-4 py-3 text-sm text-white"
            data-testid="locations-city-filter"
          >
            <option value="all">{content.city_filter_all_label}</option>
            {cities.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </Section>

      <Section className="pb-16">
        {branches.length === 0 ? (
          <div className="text-center text-[#a89878] py-16 text-sm">{content.loading_text}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-[#a89878] py-16 text-sm">
            {content.no_results_prefix} <b className="text-[#d8cdb3]">"{query}"</b>.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((b, i) => (
              <VenueCard
                key={b.id}
                branch={b}
                cityName={cityMap[b.city_id]}
                img={VENUE_IMAGES[i % VENUE_IMAGES.length]}
                slotsAvailable={(roomsByBranch[b.id] || 0) > 0}
                onWatchVideo={setVideoBranch}
              />
            ))}
          </div>
        )}
      </Section>

      <div className="border-t border-[#3a2a14]/60 bg-gradient-to-b from-[#0d0905] to-[#14100a]">
        <Section className="py-14 text-center">
          <h3 className="font-display text-2xl sm:text-3xl text-white">{content.cta_heading}</h3>
          <p className="text-[#bfb393] mt-3">
            {content.cta_subtitle}
          </p>
          <div className="flex justify-center mt-6 gap-3 flex-wrap">
            <GoldButton
              onClick={() => (window.location.href = "/franchise")}
              data-testid="locations-cta-franchise"
              className="text-base tracking-[0.15em] flex items-center gap-2"
            >
              {content.cta_franchise_btn} <ArrowRight size={18} />
            </GoldButton>
            <button
              onClick={() => (window.location.href = "/book")}
              data-testid="locations-cta-book"
              className="text-sm tracking-[0.15em] border border-[#d4a574]/40 rounded-md px-6 py-3 text-[#f4d089] hover:bg-[#1a0f06] transition"
            >
              {content.cta_book_btn}
            </button>
          </div>
        </Section>
      </div>

      <footer className="border-t border-[#3a2a14]/60 py-6 text-center text-xs text-[#a89878] leading-relaxed">
        © {new Date().getFullYear()} {content.footer_text}<br />
        BnBBuddy Property Management Services Pvt Ltd · GSTIN: 07AALCB5876H1Z8
      </footer>

      {videoBranch && (
        <VideoModal
          link={videoBranch.video_link}
          branchName={videoBranch.name}
          onClose={() => setVideoBranch(null)}
        />
      )}
    </div>
  );
};

export default Locations;
